<script lang="ts">
  /**
   * The verdict. First in every answer, in every shape, without exception.
   *
   * `04-portal-screens.md`: "The verdict sentence comes first in every case. The
   * chart supports it." That ordering is enforced structurally in
   * `Questions.svelte` — this component is rendered before the shape, never
   * inside it — because a rule that depends on five components each remembering
   * to put the sentence at the top is a rule that lasts until the sixth.
   *
   * Three signals, always all three: the glyph (shape), the word (language) and
   * the colour (hue). A bare coloured dot is not a status indicator anywhere in
   * this estate, and in greyscale the glyph and the word still carry it.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { TONE_ICON, type Verdict } from "../answers";

  let { verdict }: { verdict: Verdict } = $props();
</script>

<div class="v" data-tone={verdict.tone}>
  <p class="v__s">{verdict.sentence}</p>
  <span class="v__w">
    <Icon name={TONE_ICON[verdict.tone]} size={13} />
    {verdict.word}
  </span>
</div>

<style>
  .v {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    align-items: flex-start;
  }

  /* The heading face, because this IS the heading — the chart below it is the
     caption. Space Grotesk on an operator surface, via the surface token rather
     than a hard-coded family, so a home surface would get the serif for free. */
  .v__s {
    margin: 0;
    font-family: var(--font-heading);
    font-size: var(--fs-lead);
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.01em;
    color: var(--tx);
    max-width: 62ch;
  }

  /* Glyph + word, sharing one colour. The rule box is amber/blue/grey; the
     sentence above stays --tx so the finding never depends on hue to be read. */
  .v__w {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-1);
    padding: 3px 9px 3px 7px;
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.02em;
    box-shadow: inset 0 0 0 1px currentColor;
  }
  .v[data-tone="ok"] .v__w {
    color: var(--ok);
  }
  .v[data-tone="watch"] .v__w {
    color: var(--warn);
  }
  /* Grey, not amber: "not enough history yet" is the normal state of a young
     dataset and dressing it as a problem trains people to ignore the amber. */
  .v[data-tone="thin"] .v__w {
    color: var(--mut);
  }
</style>
